// SPDX-License-Identifier: GPL-3.0-or-later
// The boss must never hide a ball. The camera looks from the front and above, so the boss hides
// the part of the playfield behind it. This file finds the balls that the boss hides, and moves
// the opacity of the boss toward a low value while a ball is hidden. The ball shows through.
// The boss meshes decide a hit. A box around the boss only gives a quick first check, because
// the box is larger than the boss. The code has no clock, no random source and no DOM. The
// caller passes `dtMs`.

import { Box3, type Mesh, type Object3D, Ray, Raycaster, Vector3 } from "three";
import type { BallFrame } from "../../shared/contracts.ts";

/** The opacity of the boss while it hides a ball. The ball is clear through the boss at this value. */
export const FADE_MIN = 0.18;
/** The boss goes from solid to FADE_MIN (or back) in this time. */
export const FADE_MS = 180;
/**
 * The boss stays faded for this long after the last frame that hid a ball. The boss bobs with
 * a period of about 1630 ms, so a ball on the edge of the hiding area flips the decision every
 * 400 to 500 ms. A hold that is longer than half of the bob period (about 820 ms) keeps the boss
 * calm: it does not pulse. A new hide fades the boss out at once, with no hold.
 */
export const HIDE_HOLD_MS = 900;

/** What the layer needs to know about the view. */
export interface BossView {
  /** The place of the camera in the scene frame. */
  readonly camera: { readonly position: Vector3 };
  /** The balls of the frame. Table frame: x, y (down the table), z (up). */
  readonly balls: readonly BallFrame[];
  /** The ball radius in table units. */
  readonly ballRadius: number;
}

const ray = new Ray();
const hitPoint = new Vector3();
const sample = new Vector3();
const ball = new Vector3();
const modelBox = new Box3();
const rayDir = new Vector3();
const meshRay = new Raycaster();
const modelMeshes: Mesh[] = [];

/**
 * The directions of the sample points of a ball, as unit vectors in the scene frame. A point is
 * the middle, the top, or a point on one of three rings. The rings lie 30 degrees above, on the
 * middle line, and 30 degrees below. The ring on the middle line has 16 points, the others have 8.
 */
function sampleDirections(): (readonly [number, number, number])[] {
  const out: (readonly [number, number, number])[] = [
    [0, 0, 0],
    [0, 1, 0],
  ];
  const lift = Math.sin(Math.PI / 6);
  const side = Math.cos(Math.PI / 6);
  const rings: readonly (readonly [number, number, number])[] = [
    [side, lift, 8],
    [1, 0, 16],
    [side, -lift, 8],
  ];
  for (const [radius, height, count] of rings) {
    for (let k = 0; k < count; k++) {
      const a = (2 * Math.PI * k) / count;
      out.push([radius * Math.cos(a), height, radius * Math.sin(a)]);
    }
  }
  return out;
}

/** The points of a ball that must stay visible. */
const DIRECTIONS = sampleDirections();

/** True when the segment from the eye to the point crosses the box before the point. */
function crossesBox(eye: Vector3, box: Box3, point: Vector3): boolean {
  const dist = eye.distanceTo(point);
  if (dist <= 0) return false;
  ray.origin.copy(eye);
  ray.direction.copy(point).sub(eye).divideScalar(dist);
  const hit = ray.intersectBox(box, hitPoint);
  return hit !== null && hit.distanceTo(eye) < dist - 1e-3;
}

/** True when a visible mesh of the model crosses the segment from the eye to the point. */
function crossesMeshes(eye: Vector3, point: Vector3): boolean {
  const dist = eye.distanceTo(point);
  if (dist <= 0) return false;
  rayDir.copy(point).sub(eye).divideScalar(dist);
  meshRay.set(eye, rayDir);
  meshRay.far = dist - 1e-3;
  return meshRay.intersectObjects(modelMeshes, false).length > 0;
}

/** True when the boss hides one point. The box is checked first, because it is cheap. */
function hidesPoint(eye: Vector3, box: Box3, point: Vector3): boolean {
  return crossesBox(eye, box, point) && crossesMeshes(eye, point);
}

/**
 * True when the box hides a part of a ball from the eye. The ball is at `center` in the scene
 * frame (x right, y up, z toward the player). This is the box test only. It is larger than the
 * boss, so it can report a hit that the boss does not make.
 */
export function boxHidesBall(eye: Vector3, box: Box3, center: Vector3, radius: number): boolean {
  for (const [dx, dy, dz] of DIRECTIONS) {
    sample.set(center.x + dx * radius, center.y + dy * radius, center.z + dz * radius);
    if (crossesBox(eye, box, sample)) return true;
  }
  return false;
}

/** True when the boss hides a part of the ball at `center`. The meshes decide, the box limits. */
function modelHidesBall(eye: Vector3, box: Box3, center: Vector3, radius: number): boolean {
  for (const [dx, dy, dz] of DIRECTIONS) {
    sample.set(center.x + dx * radius, center.y + dy * radius, center.z + dz * radius);
    if (hidesPoint(eye, box, sample)) return true;
  }
  return false;
}

/** True when the object and every ancestor of it have the visible flag set. */
function chainVisible(object: Object3D): boolean {
  for (let o: Object3D | null = object; o !== null; o = o.parent) {
    if (!o.visible) return false;
  }
  return true;
}

/**
 * Fill the list with the meshes of the model that the camera can see. A mesh is visible only when
 * the mesh and all of its ancestors are visible. A hidden group hides its children, so a hidden
 * group cannot hide a ball. The list is reused on every call.
 */
function collectMeshes(model: Object3D): void {
  modelMeshes.length = 0;
  if (!chainVisible(model)) return;
  // `traverseVisible` skips a hidden object and all of its children.
  model.traverseVisible((o) => {
    if ((o as Mesh).isMesh === true) modelMeshes.push(o as Mesh);
  });
}

/**
 * True when the boss hides a part of any ball of the view. The model must have its world
 * matrices up to date. The boss meshes decide a hit, so a ball that sits in the empty corner of
 * the boss box does not fade the boss.
 */
export function modelHidesAnyBall(view: BossView, model: Object3D): boolean {
  if (view.balls.length === 0) return false;
  modelBox.setFromObject(model);
  if (modelBox.isEmpty()) return false;
  collectMeshes(model);
  if (modelMeshes.length === 0) return false;
  for (const b of view.balls) {
    // The table frame swaps y and z against the scene frame.
    ball.set(b.pos.x, b.pos.z, b.pos.y);
    if (modelHidesBall(view.camera.position, modelBox, ball, view.ballRadius)) return true;
  }
  return false;
}

/** Move the opacity toward its target by `dtMs`. A dt of 0 changes nothing. */
export function fadeStep(alpha: number, hiding: boolean, dtMs: number): number {
  const target = hiding ? FADE_MIN : 1;
  const step = ((1 - FADE_MIN) * Math.max(0, dtMs)) / FADE_MS;
  if (alpha < target) return Math.min(target, alpha + step);
  return Math.max(target, alpha - step);
}

/**
 * Move the hold timer by `dtMs`. A hide sets the timer to the full hold. With no hide the timer
 * counts down to 0. A dt of 0 changes nothing, so a pause keeps the hold.
 */
export function holdStep(heldMs: number, hiding: boolean, dtMs: number): number {
  if (hiding) return HIDE_HOLD_MS;
  return Math.max(0, heldMs - Math.max(0, dtMs));
}
